import { useCallback, useState } from "react";

import { useTwoFactorAction, type UseTwoFactorActionResult } from "@/shared/hooks/useTwoFactorAction";

import { PayoutBatchesService } from "../services/payoutBatches.service";
import type { PayoutBatchDetail } from "../types/payoutBatches.types";
import { type BatchError, classifyBatchError } from "../utils/batchErrors";

export interface UseResumeBatchResult {
  /** Resolves true when the run was re-queued. */
  resume: () => Promise<boolean>;
  isResuming: boolean;
  error: BatchError | null;
  reset: () => void;
  twoFactor: UseTwoFactorActionResult<PayoutBatchDetail>;
}

/**
 * `POST /:id/resume` behind the step-up prompt: rows never processed go back to the queue, paid ones stay
 * paid.
 */
export function useResumeBatch(
  batchId: string,
  applyBatch: (next: PayoutBatchDetail) => void,
): UseResumeBatchResult {
  const twoFactor = useTwoFactorAction<PayoutBatchDetail>();
  const [isResuming, setIsResuming] = useState(false);
  const [error, setError] = useState<BatchError | null>(null);
  const { start } = twoFactor;

  const resume = useCallback(async (): Promise<boolean> => {
    setError(null);
    try {
      const result = await start(async (code) => {
        setIsResuming(true);
        const { data } = await PayoutBatchesService.resume(batchId, code);
        return data;
      });
      if (!result) return false;
      applyBatch(result);
      return true;
    } catch (err: unknown) {
      setError(classifyBatchError(err));
      return false;
    } finally {
      setIsResuming(false);
    }
  }, [applyBatch, batchId, start]);

  const reset = useCallback(() => setError(null), []);

  return { resume, isResuming, error, reset, twoFactor };
}
